import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';
import { INotification, NotificationSeverity } from '../../../models/INotification';
import NotificationServiceInstance from '../../../services/NotificationService';
import CreateNotificationDialog from '../../notifications/CreateNotificationDialog';
import DateHelperInstance from '../../../services/DateHelper';

interface EventNotificationsProps {
    raceId: string;
}

function getSeverityLabel(severity: NotificationSeverity, t: (key: string) => string): string {
    switch (severity) {
        case NotificationSeverity.Information:
            return t('notifications.severityInfo');
        case NotificationSeverity.Important:
            return t('notifications.severityImportant');
        case NotificationSeverity.Danger:
            return t('notifications.severityDanger');
        default:
            return '';
    }
}

function getSeverityColor(severity: NotificationSeverity): 'info' | 'warning' | 'error' {
    switch (severity) {
        case NotificationSeverity.Information:
            return 'info';
        case NotificationSeverity.Important:
            return 'warning';
        case NotificationSeverity.Danger:
            return 'error';
        default:
            return 'info';
    }
}

function isNotificationActive(n: INotification): boolean {
    if (!n.isActive) return false;
    const now = new Date();
    const start = new Date(n.notificationStart);
    if (!DateHelperInstance.isValidDate(start) || start > now) return false;
    if (n.notificationEnd) {
        const end = new Date(n.notificationEnd);
        if (DateHelperInstance.isValidDate(end) && end < now) return false;
    }
    return true;
}

export default function EventNotifications(props: EventNotificationsProps) {
    const { raceId } = props;
    const { t } = useTranslation('common');
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingNotification, setEditingNotification] = useState<INotification | undefined>();

    const fetchNotifications = useCallback(async () => {
        const data = await NotificationServiceInstance.getEventNotifications(raceId);
        setNotifications(data);
    }, [raceId]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleCreate = () => {
        setEditingNotification(undefined);
        setDialogOpen(true);
    };

    const handleEdit = (notification: INotification) => {
        setEditingNotification(notification);
        setDialogOpen(true);
    };

    const handleFinish = async (notification: INotification) => {
        await NotificationServiceInstance.finishEventNotification(raceId, notification.notificationId, notification);
        fetchNotifications();
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingNotification(undefined);
    };

    const handleDialogCreated = () => {
        fetchNotifications();
    };

    const formatDate = (date: Date | string | null | undefined): string => {
        if (!date) return '-';
        const d = new Date(date);
        return DateHelperInstance.isValidDate(d) ? d.toLocaleString() : '-';
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    {t('notifications.createEventNotification')}
                </Button>
            </Box>
            {notifications.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t('notifications.noNotifications')}
                </Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('notifications.message')}</TableCell>
                                <TableCell>{t('notifications.severity')}</TableCell>
                                <TableCell>{t('notifications.startTime')}</TableCell>
                                <TableCell>{t('notifications.endTime')}</TableCell>
                                <TableCell>{t('notifications.status')}</TableCell>
                                <TableCell align="right">{t('notifications.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {notifications.map(n => {
                                const active = isNotificationActive(n);
                                return (
                                    <TableRow key={n.notificationId}>
                                        <TableCell>{n.description}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getSeverityLabel(n.severity, t)}
                                                color={getSeverityColor(n.severity)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(n.notificationStart)}</TableCell>
                                        <TableCell>{formatDate(n.notificationEnd)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={active ? t('notifications.active') : t('notifications.finished')}
                                                color={active ? 'success' : 'default'}
                                                size="small"
                                                variant={active ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={t('notifications.editNotification')}>
                                                <IconButton size="small" onClick={() => handleEdit(n)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {active && (
                                                <Tooltip title={t('notifications.finishNotification')}>
                                                    <IconButton size="small" onClick={() => handleFinish(n)}>
                                                        <StopIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <CreateNotificationDialog
                open={dialogOpen}
                raceId={raceId}
                notification={editingNotification}
                onClose={handleDialogClose}
                onCreated={handleDialogCreated}
            />
        </Box>
    );
}
